import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { supabase } from '../services/supabase';
import { format } from 'date-fns';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}


// 출금 처리 결과를 사용자에게 FCM 푸시로 알린다 (Supabase Edge Function).
// 실패해도 출금 처리 자체는 이미 끝난 상태이므로, 결과를 돌려주고 화면에서 사실대로 알린다.
async function notifyWithdrawal(withdrawalId) {
  try {
    const { data, error } = await supabase.functions.invoke('send-withdrawal-notifications', {
      body: { withdrawalIds: [withdrawalId] },
    });
    if (error) {
      console.error('출금 푸시 발송 실패:', error);
      return { ok: false, error: error.message || String(error) };
    }
    if (data && data.success === false) {
      return { ok: false, error: data.error || '알 수 없는 오류' };
    }
    const failures = data?.failures ?? [];
    if ((data?.sent ?? 0) === 0) {
      const reason = failures[0]?.reason === 'no_fcm_token'
        ? '사용자 앱 알림 토큰이 없습니다.'
        : (failures[0]?.reason || '알 수 없는 사유');
      return { ok: false, error: reason };
    }
    return { ok: true, error: null };
  } catch (err) {
    console.error('출금 푸시 호출 실패:', err);
    return { ok: false, error: String(err) };
  }
}

function Withdrawals() {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [adminNote, setAdminNote] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    fetchWithdrawals();
  }, []);

  const fetchWithdrawals = async () => {
    try {
      const { data, error } = await supabase
        .from('withdrawal_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // 유저 정보 별도 조회
      const userIds = [...new Set((data || []).map(w => w.user_id).filter(Boolean))];
      let usersMap = {};
      if (userIds.length > 0) {
        const { data: users } = await supabase
          .from('users')
          .select('id, name, email')
          .in('id', userIds);
        if (users) {
          usersMap = Object.fromEntries(users.map(u => [u.id, u]));
        }
      }

      const enriched = (data || []).map(w => ({
        ...w,
        user_name: usersMap[w.user_id]?.name || '-',
        user_email: usersMap[w.user_id]?.email || '-',
      }));

      setWithdrawals(enriched);
    } catch (error) {
      console.error('Failed to fetch withdrawals:', error);
      setError('출금 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleProcess = (withdrawal, action) => {
    setSelectedWithdrawal(withdrawal);
    setDialogOpen(true);
    setAdminNote('');
    setError('');
  };

  const handleSubmit = async (action) => {
    try {
      // DB 컬럼: id, user_id, amount, bank_name, account_number, account_holder, status, admin_note, processed_at, created_at, updated_at
      const updatePayload = {
        status: action,
        admin_note: adminNote || null,
        processed_at: new Date().toISOString(),
      };
      const { error: updateError } = await supabase
        .from('withdrawal_requests')
        .update(updatePayload)
        .eq('id', selectedWithdrawal.id);

      if (updateError) throw updateError;

      // If rejected, return points to user
      if (action === 'rejected') {
        const { data: currentUser } = await supabase
          .from('users')
          .select('points')
          .eq('id', selectedWithdrawal.user_id)
          .single();

        if (currentUser) {
          const currentPoints = currentUser.points || 0;
          const refundAmount = selectedWithdrawal.amount || 0;
          const newPoints = currentPoints + refundAmount;
          console.log(`포인트 반환: 현재 ${currentPoints} + 환불 ${refundAmount} = ${newPoints}`);
          const { error: pointsError } = await supabase
            .from('users')
            .update({ points: newPoints })
            .eq('id', selectedWithdrawal.user_id);

          if (pointsError) throw pointsError;
        }
      }

      // 테스트 신청에는 푸시를 보내지 않는다(실제 유저 대상이 아님).
      const base = action === 'approved'
        ? '출금이 승인되었습니다.'
        : '출금이 거절되고 포인트가 반환되었습니다.';

      if (selectedWithdrawal.is_test) {
        setSuccess(`${base} (테스트 신청이라 푸시는 보내지 않았습니다)`);
        setError('');
      } else {
        const push = await notifyWithdrawal(selectedWithdrawal.id);
        if (push.ok) {
          setSuccess(`${base} 사용자에게 푸시를 발송했습니다.`);
          setError('');
        } else {
          setSuccess(base);
          setError(`푸시 발송 실패 — ${push.error}`);
        }
      }

      setDialogOpen(false);
      fetchWithdrawals();
    } catch (error) {
      console.error('Failed to process withdrawal:', error);
      setError(`출금 처리에 실패했습니다. (${error.message || error})`);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      pending: { label: '대기중', color: 'warning' },
      approved: { label: '승인', color: 'success' },
      rejected: { label: '거절', color: 'error' },
    };

    const config = statusConfig[status] || statusConfig.pending;
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 80 },
    {
      // 테스터 계정이 만든 신청 — 실제 지급 대상이 아님
      field: 'is_test',
      headerName: '구분',
      width: 90,
      renderCell: (params) =>
        params.value ? (
          <Chip label="테스트" size="small" color="warning" variant="outlined" />
        ) : (
          <Chip label="실제" size="small" variant="outlined" />
        ),
    },
    {
      field: 'user_name',
      headerName: '사용자',
      width: 150,
      valueGetter: (value, row) => row.user_name || '-',
    },
    {
      field: 'user_email',
      headerName: '이메일',
      width: 200,
      valueGetter: (value, row) => row.user_email || '-',
    },
    {
      field: 'amount',
      headerName: '출금 포인트',
      width: 120,
      valueFormatter: (value) => value?.toLocaleString() || '0',
    },
    {
      field: 'fee',
      headerName: '수수료 (5%)',
      width: 120,
      valueGetter: (value, row) => Math.floor((row.amount || 0) * 0.05),
      valueFormatter: (value) => value?.toLocaleString() || '0',
    },
    {
      field: 'actual_amount',
      headerName: '실수령액',
      width: 120,
      valueGetter: (value, row) => {
        const amt = row.amount || 0;
        return amt - Math.floor(amt * 0.05);
      },
      valueFormatter: (value) => value?.toLocaleString() || '0',
    },
    { field: 'bank_name', headerName: '은행', width: 100 },
    { field: 'account_number', headerName: '계좌번호', width: 150 },
    { field: 'account_holder', headerName: '예금주', width: 100 },
    {
      field: 'status',
      headerName: '상태',
      width: 100,
      renderCell: (params) => getStatusChip(params.value),
    },
    {
      field: 'created_at',
      headerName: '신청일',
      width: 160,
      valueFormatter: (value) =>
        value ? format(new Date(value), 'yyyy-MM-dd HH:mm') : '',
    },
    {
      field: 'actions',
      headerName: '작업',
      width: 200,
      sortable: false,
      renderCell: (params) => {
        if (params.row.status !== 'pending') return null;
        return (
          <Box>
            <Button
              variant="contained"
              size="small"
              color="success"
              onClick={() => handleProcess(params.row, 'approved')}
              sx={{ mr: 1 }}
            >
              승인
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="error"
              onClick={() => handleProcess(params.row, 'rejected')}
            >
              거절
            </Button>
          </Box>
        );
      },
    },
  ];

  const filterWithdrawals = (status) => {
    if (status === 'all') return withdrawals;
    return withdrawals.filter((w) => w.status === status);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        출금 관리
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
        <Tab label="전체" />
        <Tab label="대기중" />
        <Tab label="승인" />
        <Tab label="거절" />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        <Box sx={{ height: 600, width: '100%' }}>
          {!loading && withdrawals.length > 0 && (
            <DataGrid
              rows={filterWithdrawals('all')}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10, page: 0 },
                },
              }}
              pageSizeOptions={[10, 25, 50]}
              disableRowSelectionOnClick
              columnVisibilityModel={{ id: false }}
            />
          )}
          {!loading && withdrawals.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography color="text.secondary">출금 요청이 없습니다.</Typography>
            </Box>
          )}
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box sx={{ height: 600, width: '100%' }}>
          {!loading && withdrawals.length > 0 && (
            <DataGrid
              rows={filterWithdrawals('pending')}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10, page: 0 },
                },
              }}
              pageSizeOptions={[10, 25, 50]}
              disableRowSelectionOnClick
              columnVisibilityModel={{ id: false }}
            />
          )}
          {!loading && withdrawals.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography color="text.secondary">출금 요청이 없습니다.</Typography>
            </Box>
          )}
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Box sx={{ height: 600, width: '100%' }}>
          {!loading && withdrawals.length > 0 && (
            <DataGrid
              rows={filterWithdrawals('approved')}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10, page: 0 },
                },
              }}
              pageSizeOptions={[10, 25, 50]}
              disableRowSelectionOnClick
              columnVisibilityModel={{ id: false }}
            />
          )}
          {!loading && withdrawals.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography color="text.secondary">출금 요청이 없습니다.</Typography>
            </Box>
          )}
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Box sx={{ height: 600, width: '100%' }}>
          {!loading && withdrawals.length > 0 && (
            <DataGrid
              rows={filterWithdrawals('rejected')}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10, page: 0 },
                },
              }}
              pageSizeOptions={[10, 25, 50]}
              disableRowSelectionOnClick
              columnVisibilityModel={{ id: false }}
            />
          )}
          {!loading && withdrawals.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography color="text.secondary">출금 요청이 없습니다.</Typography>
            </Box>
          )}
        </Box>
      </TabPanel>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>출금 처리</DialogTitle>
        <DialogContent>
          {selectedWithdrawal?.is_test && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <b>테스터 계정의 테스트 신청입니다.</b> 실제 송금하지 마세요.
              (테스터는 최소 출금 6,000P 제한을 우회할 수 있습니다)
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            사용자: {selectedWithdrawal?.user_name || '-'} ({selectedWithdrawal?.user_email || '-'})
            <br />
            출금 포인트: {selectedWithdrawal?.amount?.toLocaleString()}P
            <br />
            수수료 (5%): {selectedWithdrawal?.amount ? Math.floor(selectedWithdrawal.amount * 0.05).toLocaleString() : 0}P
            <br />
            실수령액: {selectedWithdrawal?.amount ? (selectedWithdrawal.amount - Math.floor(selectedWithdrawal.amount * 0.05)).toLocaleString() : 0}P
            <br />
            최소 출금 금액: 6,000P
            <br />
            은행: {selectedWithdrawal?.bank_name}
            <br />
            계좌번호: {selectedWithdrawal?.account_number}
            <br />
            예금주: {selectedWithdrawal?.account_holder}
          </Typography>

          <TextField
            fullWidth
            label="관리자 메모"
            value={adminNote}
            onChange={(e) => setAdminNote(e.target.value)}
            margin="normal"
            multiline
            rows={4}
            placeholder="처리 사유나 메모를 입력하세요"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>취소</Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => handleSubmit('rejected')}
          >
            거절
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => handleSubmit('approved')}
          >
            승인
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Withdrawals;
